import React, { useState } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import {
  Box, Typography, Chip, Stack, Grid, Accordion,
  AccordionSummary,
  AccordionDetails,
  ListItem,
  List,
  Button,
} from '@mui/material';
import Slider from 'react-slick';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import DomesticPackages from '../data/domesticPackage.json';
import InternationPackages from '../data/internationPackages.json';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import CheckIcon from '@mui/icons-material/Check';
import ClearIcon from '@mui/icons-material/Clear';
import EnquiryFormModal from '../components/EnquiryFormModal';
import ContactSupportIcon from '@mui/icons-material/ContactSupport';

const DomAndIntPackageDetails = () => {
  const location = useLocation();
  const [openEnquiry, setOpenEnquiry] = useState(false);
  const tripType = location.pathname.split('/')[1];
  const { destinationId, packageId } = useParams();
  const packages = tripType === 'internationaltrips' ? [...InternationPackages] : [...DomesticPackages];

  const destinationFromPackageJson = packages.find(p => p.name === destinationId);
  const flatPackage = packages.find(p => p._id === packageId || p.name === destinationId);

  // Determine which package to use
  let selectedPackage = null;

  if (destinationFromPackageJson?.packages) {
    selectedPackage = destinationFromPackageJson.packages.find(p => p.name === packageId);
  }

  if (!selectedPackage && flatPackage) {
    selectedPackage = flatPackage;
  }

  if (!selectedPackage) {
    return (
      <Box p={4}>
        <Typography variant="h5" color="error">Package not found</Typography>
      </Box>
    );
  }
  const sliderSettings = {
    dots: true,
    customPaging: (i) => (
      <img
        src={selectedPackage.image[i]}
        alt={`thumb-${i}`}
        style={{
          width: 30,
          height: 30,
          objectFit: 'cover',
          borderRadius: 4,
          border: '2px solid transparent',
        }}
      />
    ),
    dotsClass: 'slick-dots slick-thumb',
    infinite: true,
    speed: 600,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: false,
    autoplay: true,
    autoplaySpeed: 3000,
  };

  return (
    <Box py={{ xs: 7, md: 10, lg: 10 }} px={3}>
      <Grid container spacing={5} py={5}>
        {/* Image Slider */}
        <Grid item size={{ xs: 12, sm: 6, md: 8, lg: 8 }}>
          <Box mb={8}>
            <Slider {...sliderSettings}>
              {selectedPackage?.image?.map((img, index) => (
                <Box key={index} sx={{ borderRadius: 2, overflow: 'hidden' }}>
                  <img
                    src={img}
                    alt={`slide-${index}`}
                    style={{
                      width: '100%',
                      height: 'auto',
                      maxHeight: 400,
                      objectFit: 'cover',
                      borderRadius: 8,
                    }}
                  />
                </Box>
              ))}
            </Slider>
          </Box>

          <Box py={2}  display="flex">
            <Typography px={1} sx={{fontWeight:'600',}}>Slogan:</Typography>
            <Typography>{selectedPackage?.slogan}</Typography>
          </Box>

          <Box p={3} sx={{
            backgroundColor: '#fff',
            border: '1px solid #e0e0e0',
            borderRadius: 2,
            boxShadow: 1,
          }}>
            {/* Overview */}
            <Typography variant="h5" px={2} gutterBottom sx={{ borderLeft: "4px solid green" }}>
              Package Overview
            </Typography>
            <List>
              {selectedPackage?.overview?.map((item, index) => (
                <ListItem key={index} disableGutters>
                  <Box display="flex" alignItems="flex-start">
                    <FiberManualRecordIcon
                      fontSize="small"
                      sx={{ mt: '4px', color: 'green', mr: 1 }}
                    />

                    <Box>
                      <Typography fontWeight="bold">{item.title}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {item.description}
                      </Typography>
                    </Box>
                  </Box>
                </ListItem>
              ))}
            </List>
          </Box>


          <Box py={3}>
            {/* Iternity */}
            {selectedPackage?.itinerary && (
              <Box mt={6} p={3} sx={{
                backgroundColor: '#fff',
                border: '1px solid #e0e0e0',
                borderRadius: 2,
                boxShadow: 1,
              }}>
                <Typography variant="h5" px={2} gutterBottom sx={{ borderLeft: "4px solid green" }}>
                  Day Wise Iternity
                </Typography>
                {Object.entries(selectedPackage?.itinerary)?.map(([day, detail], idx) => (
                  <Accordion key={idx} defaultExpanded={idx === 0} sx={{
                    my: 2,
                    borderRadius: 2,
                    overflow: 'hidden',
                    boxShadow: 2
                  }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography variant="subtitle1" fontWeight="bold">
                        {`Day ${idx + 1}`}
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Typography variant="body2">{detail}</Typography>
                    </AccordionDetails>
                  </Accordion>
                ))}
              </Box>
            )}
          </Box>


          <Box>
            <Grid container spacing={5}>
              <Grid p={1} item size={{ xs: 12, lg: 6 }} sx={{
                backgroundColor: '#D6F6D5',
                border: '1px solid #e0e0e0',
                borderRadius: 2, maxHeight: 150,
                minHeight: 150,
                boxShadow: 1,
              }}>
                <Box sx={{
                  overflowY: 'auto', maxHeight: 130,
                  minHeight: 130,
                }}>
                  {selectedPackage?.inclusions?.map((inclusion, index) => (
                    <Box p={0.3} key={index} display="flex">
                      <Box pr={1}>
                        <CheckIcon sx={{ fontSize: '1rem', color: 'green' }} />
                      </Box>
                      {inclusion}
                    </Box>
                  ))}
                </Box>
              </Grid>

              <Grid p={1} item size={{ xs: 12, lg: 6 }} sx={{
                backgroundColor: '#FFDCD1',
                border: '1px solid #e0e0e0',
                borderRadius: 2, maxHeight: 150,
                minHeight: 150,
                boxShadow: 1,
              }}>
                <Box sx={{
                  overflowY: 'auto', maxHeight: 130,
                  minHeight: 130,
                }}>
                  {selectedPackage?.exclusions?.map((exclusion, index) => (
                    <Box p={0.3} key={index} display="flex">
                      <Box pr={1}>
                        <ClearIcon sx={{ fontSize: '1rem', color: 'red' }} />
                      </Box>
                      {exclusion}
                    </Box>
                  ))}
                </Box>
              </Grid>
            </Grid>
          </Box>



        </Grid>

        {/* Package Info */}
        <Grid item size={{ xs: 12, sm: 6, md: 4, lg: 4 }}>
          <Box
            sx={{
              boxShadow: 4,
              borderRadius: 2,
            }}
          >
            <Box sx={{ background: 'linear-gradient(to right, #cefad0, #0e8279)', padding: 2, }}>
              <Typography variant="h4" mb={2}>
                {selectedPackage.name}
              </Typography>
              <Typography variant="body1" mb={1}>
                <strong>Duration:</strong> {selectedPackage.duration}
              </Typography>
              <Typography variant="body1" mb={1}>
                <strong>Price:</strong> {selectedPackage.price}
              </Typography>
              <Typography variant="body1" mb={1}>
              <strong> {selectedPackage.tagline}</strong>
              </Typography>
              {selectedPackage.inclusion?.length > 0 && (
                <>
                  <Typography variant="h6" mt={1}>Inclusions:</Typography>
                  <Stack direction="row" spacing={1} mt={1} mb={2} flexWrap="wrap">
                    {selectedPackage?.inclusion?.map((item, idx) => (
                      <Chip key={idx} label={item} sx={{ backgroundColor: "#0e8279", color: "white" }} />
                    ))}
                  </Stack>
                </>
              )}
            </Box>
            <Box sx={{ padding: 2 }}>
              <Button variant='outlined' sx={{
                color: '#0e8279',
                borderRadius: '25px',
                width: '100%',
                borderColor: "#0e8279",
                fontWeight: 600
              }} onClick={() => setOpenEnquiry(true)}>Enquire Now</Button>
              <EnquiryFormModal open={openEnquiry} setOpen={setOpenEnquiry} />
            </Box>
          </Box>

          <Box pt={4}>
            <Box p={2} sx={{
              boxShadow: 4,
              borderRadius: 2,
            }}>
              <Typography display="flex">
                <Box>
                </Box>  {selectedPackage.metaDescription}
              </Typography>

            </Box>
          </Box>

          {/* <Box pt={4}>
            <Box p={2} sx={{
              boxShadow: 4,
              borderRadius: 2,
            }}>
              <Typography display="flex">
                <Box>
                  <AccessTimeIcon sx={{ color: 'red', px: "2px" }} />
                </Box> Duration : {"  "} {selectedPackage.duration}
              </Typography>

              <Typography display="flex">
                <Box>
                  <LocationPinIcon sx={{ color: 'red', px: "2px" }} />
                </Box>
                <Box>
                  Place to visit:  {"  "}
                  {selectedPackage.placetovisit}
                </Box>
              </Typography>
            </Box>
          </Box> */}

          <Box pt={4} >
            <Box p={2} sx={{
              boxShadow: 4,
              borderRadius: 2,
              backgroundColor: 'lightgrey'
            }}>
              <Typography sx={{ fontSize: '1.2rem', fontWeight: 600 }}>
                Need Help               <ContactSupportIcon />
              </Typography>
              <Typography>
                Call us: 9643751779, 7042104648
              </Typography>
              <Typography>
                Email: info@dreamflyholiday.com
              </Typography>
            </Box>
          </Box>

        </Grid>
      </Grid>

    </Box>

  );
};

export default DomAndIntPackageDetails;
